import React, {useState} from 'react';
import classNames from "classnames";
import intl from "react-intl-universal";

import {useRootStore, useDsManager} from "@stores/AppStateContext";
import {useBasicDataSourceDeps} from "../../../platform/hooks/useDataSourceDependencies";

import {Location} from "../../../stores/reliability/types";
import {deleteLocation} from "../../../actions/location/deleteLocation";
import {DeleteIcon} from "../../components/icons/DeleteIcon";
import {Tooltip} from "../../components/Tooltip/Tooltip";
import {ModalConfirm} from "../../components/ModalConfirm/ModalConfirm";

export interface LocationRowActionsProps {
    location: Location;
    className?: string;
}

/**
 * Per-row delete for the Locations table. Calls the deleteLocation action
 * (provider + snackbar), then removes the row locally — subscriptions don't fire
 * in the mock backend, so onDeleteLocation alone won't update the list there.
 */
export const LocationRowActions = ({location, className}: LocationRowActionsProps) => {
    const rootStore = useRootStore();
    const dsManager = useDsManager();
    const dsDeps = useBasicDataSourceDeps();
    const [confirmOpen, setConfirmOpen] = useState(false);

    const wsId = rootStore.view.workspace?.id;

    const handleDelete = async () => {
        if (!wsId) return;
        await deleteLocation(dsDeps.dataClientService, dsDeps.authService, wsId, location.id);
        dsManager?.getLocationDS(wsId).removeLocation(location.id);
        setConfirmOpen(false);
    };

    return (
        <div
            className={classNames({[className!]: className})}
            onClick={(e) => e.stopPropagation()}
        >
            <Tooltip title={intl.get('common.remove').d('Remove')}>
                <DeleteIcon
                    size={20}
                    className={'Locations-icon'}
                    onClick={() => setConfirmOpen(true)}
                />
            </Tooltip>
            <ModalConfirm
                open={confirmOpen}
                type={'warning'}
                header={intl.get('locations.delete.header').d('Delete location')}
                confirmText={intl.get('common.delete').d('Delete')}
                onConfirm={handleDelete}
                onCancel={() => setConfirmOpen(false)}
                onClose={() => setConfirmOpen(false)}
            >
                <div className="body-medium">
                    {intl.get('locations.delete.body').d('Are you sure you want to delete this location? This cannot be undone.')}
                </div>
            </ModalConfirm>
        </div>
    );
};
